import React from 'react';
import { Box, Button, Stack, Text, TextInput, ThemeIcon, Title } from '@mantine/core';
import { IconFingerprint } from '@tabler/icons-react';
import { useBeAuthContext } from '@besuites/auth/react';
import FlowAlert from '@/components/auth/flow/components/parts/Flow.Alert';
import FlowAlternatives from '@/components/auth/flow/components/parts/Flow.Alternatives';
import passkeyProblemOf from '@/function/auth/passkey.problem.of';
import passkeyDeviceName, { passkeyNameClean, PASSKEY_NAME_MAX } from '@/function/auth/passkey.device.name';
import type { TypeFlowScreenProps } from '@/types/auth/types.auth.flow';

type Props = TypeFlowScreenProps & { step: string; registering: boolean; ceremony: boolean; passkeySupported: boolean };

const FlowPasskey: React.FC<Props> = ({ flow, message, step, registering, ceremony, passkeySupported }) => {
    const { browser } = useBeAuthContext();
    const [problem, setProblem] = React.useState<string | null>(null);
    const [working, setWorking] = React.useState(false);
    const [name, setName] = React.useState(() => passkeyDeviceName(typeof navigator === 'undefined' ? '' : navigator.userAgent));
    const busy = working || flow.busy;

    const go = async () => {
        if (busy) return;
        setProblem(null);
        if (!ceremony) {
            await flow.submit(step, {});
            return;
        }
        setWorking(true);
        const outcome = registering ? await browser.passkey.register(flow.emitted.options) : await browser.passkey.assert(flow.emitted.options);
        setWorking(false);
        if (outcome.status !== 'ok') {
            setProblem(passkeyProblemOf(outcome));
            return;
        }
        const label = passkeyNameClean(name).trim();
        await flow.submit(step, registering && label ? { response: outcome.credential, name: label } : { response: outcome.credential });
    };

    return (
        <Stack align="center" gap="lg">
            <ThemeIcon size={64} radius="xl" variant="light" color="teal">
                <IconFingerprint size={34} />
            </ThemeIcon>
            <Box style={{ textAlign: 'center', maxWidth: 380 }}>
                <Title order={3}>{registering ? 'Nuova passkey' : 'Passkey'}</Title>
                <Text c="dimmed" size="sm" mt={6}>
                    {registering
                        ? 'Con una passkey entri con l’impronta, il volto o il PIN del dispositivo, senza password.'
                        : 'Il browser ti chiederà l’impronta, il volto o il PIN del dispositivo.'}
                </Text>
            </Box>
            <Box w="100%">{problem ? <FlowAlert message={{ field: null, text: problem }} /> : <FlowAlert message={message} />}</Box>
            {registering && ceremony ? (
                <TextInput
                    w="100%"
                    label="Nome della passkey"
                    description="Ti aiuta a riconoscerla fra le tue passkey."
                    maxLength={PASSKEY_NAME_MAX}
                    autoComplete="off"
                    value={name}
                    disabled={busy}
                    onChange={(event) => setName(passkeyNameClean(event.currentTarget.value))}
                />
            ) : null}
            <Button fullWidth radius="md" loading={busy} leftSection={<IconFingerprint size={18} />} disabled={!passkeySupported || busy} onClick={() => void go()}>
                {problem ? 'RIPROVA' : registering ? 'CREA LA PASSKEY' : 'CONTINUA CON LA PASSKEY'}
            </Button>
            <FlowAlternatives flow={flow} current="passkey" passkeySupported={passkeySupported} />
        </Stack>
    );
};

export default FlowPasskey;
